import { h } from '@omni-torn/ui';
import { BAZAAR_KEEP_MS, itemCategories, priceSummary, readBazaarReply, searchItems, sellSuggestions, type BazaarReply, type ItemMarketAnswer } from '@omni-torn/shared';
import type { RefItem, TornRefAnswer } from '@omni-torn/shared/torn-ref';
import { extensionBazaar } from '../bridge';
import { navigateTo } from '../nav';
import { server, session } from '../session';
import { ago, box, ext, facts, kids, loading, money, num, problem, table } from '../ui';
import { ref } from './torn';
import { dealsView, gearView, marketTabs } from './market-pool';

/**
 * Market (website roadmap S11 — weav3r's price finder and categories): search an item, then its Item Market listings
 * (the cheapest 100), its bazaar listings, Torn's market value and average price, and what to list it for; or browse every
 * item by category.
 *
 * - Item Market: `GET /api/market/item/:id` — the Omni-Torn server reads Torn's public answer on demand (lent keys first,
 *   else its own key) and keeps it 3 minutes; the player's key isn't used.
 * - Bazaars: weav3r.dev's public list, ONLY through the player's own Omni-Torn extension (site bridge `bazaar`, its weav3r
 *   connection). Kept on this page (10 minutes, in memory) — never sent to the Omni-Torn server or the player database.
 * - Items, types and market values: Torn's items list the server already keeps (`/api/torn/items`, the Torn → Items page).
 */

const MARKET_URL = (id: number) => `https://www.torn.com/page.php?sid=ItemMarket#/market/view=search&itemID=${id}`;
const BAZAAR_URL = (player: number, item: number) => `https://www.torn.com/bazaar.php?userId=${player}&itemId=${item}#/`;
const WEAV3R_URL = 'https://weav3r.dev/';

/** This visit's bazaar answers (10 minutes), so going back to an item doesn't ask again. */
const bazaarKept = new Map<number, { at: number; reply: BazaarReply | null }>();
/** The player's sell options, for this visit. */
const sellOpts = { percent: 100, fee: 0 };

const pct = (p: number | null): string => (p === null ? '—' : `${p > 0 ? '+' : p < 0 ? '−' : '±'}${num(Math.abs(p), 1)}%`);
const vs = (price: number, value: number | null): number | null => (value ? Math.round(((price - value) / value) * 1000) / 10 : null);
const vsCell = (price: number, value: number | null) => {
  const p = vs(price, value);
  return h('span', { class: p === null ? 'muted' : p < 0 ? 't-green' : p > 0 ? 't-red' : '' }, pct(p));
};

export async function renderMarket(view: HTMLElement, path: string): Promise<void> {
  const id = Number(/^\/market\/(\d+)\/?$/.exec(path)?.[1] ?? 0) || null;
  const sub = /^\/market\/(gear|deals)\/?$/.exec(path)?.[1] as 'gear' | 'deals' | undefined;
  const body = h('div', {}, loading('Reading Torn’s items…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Market')), marketTabs(sub ?? 'prices'), body);
  let items: TornRefAnswer<'items'>;
  try {
    items = await ref('items');
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  // Gear (S31) and Deals (S32): Omni-Torn's market pool (pages/market-pool.ts).
  if (sub === 'gear') return gearView(body, items.data);
  if (sub === 'deals') return dealsView(body, items.data);
  const byId = new Map(items.data.map((i) => [i.id, i]));
  const search = searchForm(items.data);
  if (!id) {
    body.replaceChildren(search, categoriesBox(items.data, new URLSearchParams(location.search).get('cat') ?? ''));
    return;
  }
  const item = byId.get(id);
  if (!item) {
    body.replaceChildren(search, box('No such item', h('p', {}, `Torn has no item ${id}. `, h('a', { href: '/market', 'data-link': true }, 'Back to every item'))));
    return;
  }
  const holder = h('div', {});
  body.replaceChildren(search, holder);
  if (item.tradable === false) {
    holder.replaceChildren(itemHead(item), box('Prices', facts([['Market value (Torn)', money(item.marketValue)], ['Item Market and bazaars', 'This item can’t be traded, so nobody can list it.']]), { id: 'market-prices' }));
    return;
  }
  await itemView(holder, item);
}

// ---------------------------------------------------------------- search

function searchForm(items: readonly RefItem[]): HTMLElement {
  const input = h('input', { id: 'market-search', type: 'search', placeholder: 'Item name or id…', maxlength: '40', autocomplete: 'off', 'aria-label': 'Item name or id' }) as HTMLInputElement;
  const list = h('ul', { class: 'suggestions', id: 'market-suggestions', role: 'listbox', hidden: true }) as HTMLUListElement;
  const form = h('form', { class: 'search market-search', role: 'search', autocomplete: 'off', 'data-market': 'search' }, input, h('button', { type: 'submit' }, 'Prices'), list);
  const found = () => searchItems(items, input.value, 12);
  const show = () => {
    const r = found();
    list.replaceChildren(
      ...r.map((i) => h('li', { role: 'option' }, h('a', { href: `/market/${i.id}`, 'data-link': true }, i.name, h('span', { class: 'muted' }, ` [${i.id}] · ${i.type}${i.marketValue ? ` · ${money(i.marketValue)}` : ''}`)))),
      ...(input.value.trim() && !r.length ? [h('li', { class: 'muted empty' }, 'No item by that name.')] : []),
    );
    list.hidden = !list.childElementCount;
  };
  input.addEventListener('input', show);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const r = found();
    const exact = r.find((i) => i.name.toLowerCase() === input.value.trim().toLowerCase()) ?? (r.length === 1 ? r[0] : null);
    if (exact) navigateTo(`/market/${exact.id}`);
    else show();
  });
  return form;
}

// ---------------------------------------------------------------- categories

function categoriesBox(items: readonly RefItem[], start: string): HTMLElement {
  const cats = itemCategories(items);
  let cat = cats.some((c) => c.type === start) ? start : '';
  let tradableOnly = true;
  const out = h('div', {});
  const pick = h('div', { class: 'cat-list', role: 'list' });
  const draw = () => {
    pick.replaceChildren(
      ...cats.map((c) =>
        h('button', { type: 'button', role: 'listitem', class: `btn-small${c.type === cat ? ' active' : ''}`, 'data-cat': c.type, 'aria-pressed': String(c.type === cat), onclick: () => ((cat = c.type), history.replaceState(null, '', `/market?cat=${encodeURIComponent(c.type)}`), draw()) }, `${c.type} `, h('span', { class: 'muted' }, String(c.count))),
      ),
    );
    if (!cat) return out.replaceChildren(h('p', { class: 'muted' }, `${num(items.length)} items in ${cats.length} categories. Pick one, or search for an item above.`));
    const list = items.filter((i) => i.type === cat && (!tradableOnly || i.tradable !== false));
    out.replaceChildren(
      table(
        list,
        [
          { label: 'Item', get: (i) => h('span', {}, h('a', { href: `/market/${i.id}`, 'data-link': true }, i.name), h('span', { class: 'muted small' }, ` [${i.id}]`), i.subType ? h('div', { class: 'muted small' }, i.subType) : null), sort: (i) => i.name },
          { label: 'Market value', get: (i) => money(i.marketValue), sort: (i) => i.marketValue ?? 0, cls: 'r' },
          { label: 'Shop price', get: (i) => money(i.buyPrice), sort: (i) => i.buyPrice ?? 0, cls: 'r' },
          { label: 'Circulation', get: (i) => num(i.circulation), sort: (i) => i.circulation ?? 0, cls: 'r' },
          { label: '', get: (i) => ext(MARKET_URL(i.id), 'Item Market') },
        ],
        { initial: 1, remember: 'market-cat', empty: 'No items here.' },
      ),
    );
  };
  const tradable = h('input', { type: 'checkbox', id: 'market-tradable', checked: true }) as HTMLInputElement;
  tradable.addEventListener('change', () => ((tradableOnly = tradable.checked), draw()));
  draw();
  return box('Browse by category', [pick, h('div', { class: 'toolbar' }, h('label', { class: 'check' }, tradable, ' Tradable only')), out], { id: 'market-categories' });
}

// ---------------------------------------------------------------- one item

function itemHead(item: RefItem): HTMLElement {
  return h(
    'p',
    { class: 'market-item-head' },
    h('a', { href: `/market?cat=${encodeURIComponent(item.type)}`, 'data-link': true }, `← ${item.type}`),
    ' · ',
    h('strong', { 'data-market-item': String(item.id) }, item.name),
    h('span', { class: 'muted' }, ` [${item.id}]${item.subType ? ` · ${item.subType}` : ''}`),
    ' · ',
    ext(MARKET_URL(item.id), 'Item Market on Torn'),
    item.tradable === false ? h('span', { class: 'muted' }, ' · untradable') : null,
  );
}

async function itemView(holder: HTMLElement, item: RefItem): Promise<void> {
  const signIn = session.get();
  const summary = h('div', {}, loading('Reading the Item Market…'));
  const sell = h('div', {});
  const marketBox = h('div', {}, loading('Reading the Item Market…'));
  const bazaarBox = h('div', {}, loading('Asking your extension for bazaar listings…'));
  holder.replaceChildren(
    itemHead(item),
    box('Prices', [summary, sell], { id: 'market-prices' }),
    box('Item Market', marketBox, { id: 'market-listings' }),
    box('Bazaars', bazaarBox, { id: 'market-bazaars' }),
  );

  let market: ItemMarketAnswer | null = null;
  let marketProblem: unknown = null;
  let bazaar: BazaarReply | null = null;
  let bazaarState: 'asking' | 'none' | 'done' = 'asking';

  const drawSummary = () => {
    const s = priceSummary(market?.listings ?? [], bazaar?.listings ?? [], item.marketValue, market?.average ?? null);
    summary.replaceChildren(
      facts([
        ['Market value (Torn)', money(s.value)],
        ['Average price (Torn)', market ? money(s.average) : '…'],
        ['Cheapest on the Item Market', market ? money(s.market) : marketProblem ? '—' : '…'],
        ['Cheapest in a bazaar', bazaarState === 'asking' ? '…' : money(s.bazaar)],
        ['Cheapest anywhere', s.cheapest ? h('span', { 'data-cheapest': s.cheapest.where }, `${money(s.cheapest.price)} (${s.cheapest.where === 'market' ? 'Item Market' : 'bazaar'}) `, h('span', { class: 'muted' }, s.vsValue === null ? '' : `${pct(s.vsValue)} vs value`)) : '—'],
        ['Units listed (Item Market)', market ? `${num(s.marketUnits)} in the cheapest ${num(market.listings.length)} listings · median ${money(s.marketMedian)}` : '…'],
      ]),
    );
  };

  const drawSell = () => {
    const rows = sellSuggestions(market?.listings ?? [], item.marketValue, { percent: sellOpts.percent, feePct: sellOpts.fee });
    const numIn = (val: number, min: number, max: number, label: string, set: (n: number) => void) => {
      const i = h('input', { type: 'number', min: String(min), max: String(max), step: '1', value: String(val), class: 'num-input market-num', 'aria-label': label }) as HTMLInputElement;
      i.addEventListener('change', () => {
        const n = Math.round(Number(i.value));
        if (Number.isFinite(n) && n >= min && n <= max) {
          set(n);
          drawSell();
        }
      });
      return i;
    };
    sell.replaceChildren(
      h('h3', { class: 'sub' }, 'What to list it for'),
      h('div', { class: 'toolbar' }, h('label', {}, '% of market value ', numIn(sellOpts.percent, 1, 1000, 'Percent of market value', (n) => (sellOpts.percent = n))), h('label', {}, 'Your selling fee % ', numIn(sellOpts.fee, 0, 50, 'Your selling fee percent', (n) => (sellOpts.fee = n)))),
      rows.length
        ? table(
            rows,
            [
              { label: 'Suggestion', get: (r) => h('span', { 'data-sell': r.id }, r.label) },
              { label: 'List at', get: (r) => money(r.price), cls: 'r' },
              { label: 'You get', get: (r) => money(r.youGet), cls: 'r' },
              { label: 'vs value', get: (r) => vsCell(r.price, item.marketValue), cls: 'r' },
              { label: '', get: (r) => h('span', { class: 'muted small' }, r.note) },
            ],
            { empty: '' },
          )
        : h('p', { class: 'muted' }, market || marketProblem ? 'Nothing to go on: no listings and no market value.' : 'Waiting for the Item Market…'),
    );
  };

  const drawMarket = () => {
    if (marketProblem) return marketBox.replaceChildren(problem(marketProblem), h('button', { type: 'button', class: 'btn-small', onclick: () => void loadMarket() }, 'Try again'));
    if (!market) return;
    let units = 0;
    const rows = market.listings.map((l, i) => ({ ...l, n: i + 1, cum: (units += l.amount) }));
    marketBox.replaceChildren(
      table(
        rows,
        [
          { label: '#', get: (r) => String(r.n), sort: (r) => r.n, cls: 'r' },
          { label: 'Price', get: (r) => money(r.price), sort: (r) => r.price, cls: 'r' },
          { label: 'Amount', get: (r) => num(r.amount), sort: (r) => r.amount, cls: 'r' },
          { label: 'vs value', get: (r) => vsCell(r.price, item.marketValue), sort: (r) => r.price, cls: 'r' },
          { label: 'Units up to here', get: (r) => num(r.cum), sort: (r) => r.cum, cls: 'r' },
        ],
        { initial: 1, desc: false, empty: 'Nobody is selling it on the Item Market right now.' },
      ),
      h('p', { class: 'muted small', 'data-note': 'market' }, `The cheapest ${num(market.listings.length)} listings from Torn’s Item Market, read ${ago(market.at)} (kept 3 minutes, then read again when someone opens it). Prices change fast — `, ext(MARKET_URL(item.id), 'see it on Torn'), '.'),
    );
  };

  const drawBazaar = () => {
    if (bazaarState === 'asking') return;
    if (bazaarState === 'none') {
      return bazaarBox.replaceChildren(
        h('p', { class: 'muted', 'data-note': 'bazaar-off' }, 'Bazaar listings show here when the Omni-Torn extension is installed and signed in in this browser: it gets them from ', ext(WEAV3R_URL, 'weav3r.dev'), ' with its own connection, and they stay on this page.'),
      );
    }
    if (bazaar?.problem && !bazaar.listings.length) return bazaarBox.replaceChildren(h('p', { class: 'muted', 'data-note': 'bazaar-problem' }, `No bazaar listings: ${bazaar.problem}`));
    bazaarBox.replaceChildren(
      table(
        bazaar?.listings ?? [],
        [
          { label: 'Seller', get: (l) => ext(BAZAAR_URL(l.playerId, item.id), l.playerName ?? `#${l.playerId}`), sort: (l) => (l.playerName ?? '').toLowerCase() },
          { label: 'Price', get: (l) => money(l.price), sort: (l) => l.price, cls: 'r' },
          { label: 'Amount', get: (l) => num(l.qty), sort: (l) => l.qty, cls: 'r' },
          { label: 'vs value', get: (l) => vsCell(l.price, item.marketValue), sort: (l) => l.price, cls: 'r' },
          { label: 'Seen', get: (l) => (l.updated ? h('span', { class: 'muted small' }, ago(l.updated * 1000)) : '—'), sort: (l) => l.updated ?? 0, cls: 'r' },
        ],
        { initial: 1, desc: false, empty: 'No bazaar listings known for this item.' },
      ),
      h('p', { class: 'muted small', 'data-note': 'bazaar' }, 'From ', ext(WEAV3R_URL, 'weav3r.dev'), ' (TornW3B), through your Omni-Torn extension — they stay on this page. A bazaar can sell out or change its price before you get there.'),
    );
  };

  const drawAll = () => {
    drawSummary();
    drawSell();
    drawMarket();
    drawBazaar();
  };

  const loadMarket = async () => {
    marketProblem = null;
    marketBox.replaceChildren(loading('Reading the Item Market…'));
    try {
      market = await server<ItemMarketAnswer>('GET', `/api/market/item/${item.id}`);
    } catch (err) {
      marketProblem = err;
    }
    if (holder.isConnected) drawAll();
  };

  const loadBazaar = async () => {
    const kept = bazaarKept.get(item.id);
    if (kept && Date.now() - kept.at < BAZAAR_KEEP_MS) {
      bazaar = kept.reply;
    } else {
      bazaar = signIn ? readBazaarReply(await extensionBazaar(item.id, signIn.playerId).catch(() => null), item.id) : null;
      if (bazaar && !bazaar.problem) bazaarKept.set(item.id, { at: Date.now(), reply: bazaar });
    }
    bazaarState = bazaar ? 'done' : 'none';
    if (holder.isConnected) drawAll();
  };

  drawAll();
  await Promise.all([loadMarket(), loadBazaar()]);
  if (!holder.isConnected) return;
  holder.append(...kids(h('p', { class: 'muted small' }, 'Market value is Torn’s own (from its items list). Item Market listings come from Torn’s API through the Omni-Torn server — your key isn’t used. Bazaar listings from weav3r.dev never reach the Omni-Torn server; what Omni-Torn users saw on bazaars themselves is on Market → Deals and Gear.')));
}
